import { ApplicationStatus } from 'modules/teams/domain/enums/application-status';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { selectedStageSelector } from 'modules/championship/store/stages';
import { useChampionship } from './use-championship';
import { useUpsertStage } from './use-upsert-stage';

/**
 * Вычищает из выбранного этапа команды, у которых есть непринятые заявки.
 *
 * IMPORTANT: два предохранителя от бесконечного цикла. `upsertStage.fulfilled`
 * кладёт в стор новый объект этапа, поэтому эффект перезапускается после каждого
 * сохранения. Значит (1) состав после фильтра обязан стать короче исходного и
 * (2) один и тот же состав второй раз не отправляется — иначе, если сервер вернёт
 * этап не с тем составом, что мы просили, запрос уйдёт по кругу.
 */
export const useCheckAcceptedTeams = (championshipId: number) => {
  const upsertStage = useUpsertStage();
  const { data: championship } = useChampionship(championshipId);
  const stage = useSelector(selectedStageSelector);
  /** Подпись последнего отправленного состава — `stageId:teamIds`. */
  const requestedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!stage?.teams.length) return;

    const notAcceptedTeamIds = new Set(
      (championship?.applications ?? [])
        .filter((application) => application.status !== ApplicationStatus.ACCEPTED)
        .map((application) => application.teamId),
    );

    if (!notAcceptedTeamIds.size) return;

    const teamIds = stage.teams.filter((team) => !notAcceptedTeamIds.has(team.id)).map((team) => team.id);

    if (teamIds.length === stage.teams.length) return;

    const requested = `${stage.id}:${teamIds.join(',')}`;
    if (requestedRef.current === requested) return;
    requestedRef.current = requested;

    upsertStage({
      id: stage.id,
      championshipId: stage.championshipId,
      name: stage.name,
      type: stage.type,
      teamIds,
    });
  }, [stage, championship, upsertStage]);
};

export default useCheckAcceptedTeams;
